// Relative path: src/components/ReviewMetaLine.js
import React from 'react';
import InfoTooltip from './InfoTooltip';

function getSatisfactionLabel(level) {
  if (typeof level === 'string') {
    level = parseInt(level, 10);
  }
  let label, colorClass;
  switch (true) {
    case level === 5:
      label = 'super';
      colorClass = 'text-mm_green bg-transparent';
      break;
    case level === 4:
      label = 'high';
      colorClass = 'text-mm_green bg-transparent';
      break;
    case level === 3:
      label = 'mid';
      colorClass = 'text-orange-400 bg-transparent';
      break;
    case level < 3 && level >= 0:
      label = 'low';
      colorClass = 'text-red-600 bg-transparent';
      break;
    default:
      label = 'unknown satisfaction';
      colorClass = 'text-gray-500 bg-transparent';
  }
  return { label, colorClass };
}

function getSatisfactionTooltip(level) {
  if (typeof level === 'string') {
    level = parseInt(level, 10);
  }
  switch (true) {
    case level === 5:
      return "The reviewer was very satisfied.";
    case level === 4:
      return "The reviewer was satisfied.";
    case level === 3:
      return "The reviewer had mixed opinions.";
    case level < 3 && level >= 0:
      return "The reviewer was not satisfied.";
    default:
      return "Unknown satisfaction level.";
  }
}

function getSimilarityLabel(level) {
  // Make sure we're working with a number
  if (typeof level === 'string') {
    level = parseFloat(level);
  }
  
  let label, colorClass;
  switch (true) {
    case level >= 0.9:
      label = 'super';
      colorClass = 'text-mm_green bg-transparent';
      break;
    case level >= 0.8 && level < 0.9:
      label = 'high';
      colorClass = 'text-mm_green bg-transparent';
      break;
    case level >= 0.6 && level < 0.8:
      label = 'mid';
      colorClass = 'text-orange-400 bg-transparent';
      break;
    case level < 0.6 && level >= 0:
      label = 'low';
      colorClass = 'text-red-600 bg-transparent';
      break;
    default:
      label = 'unknown similarity';
      colorClass = 'text-gray-500 bg-transparent';
      break;
  }
  return { label, colorClass };
}

function getSimilarityTooltip(level) {
  // Make sure we're working with a number
  if (typeof level === 'string') {
    level = parseFloat(level);
  }
  
  switch (true) {
    case level >= 0.9:
      return "Reviewer is very similar to you.";
    case level >= 0.8 && level < 0.9:
      return "Reviewer is generally similar to you.";
    case level >= 0.6 && level < 0.8:
      return "Reviewer is moderately similar to you.";
    case level < 0.6 && level >= 0:
      return "Reviewer has low similarity with you.";
    default:
      return "We do not have enough data about similarity.";
  }
}

export default function ReviewMetaLine({ similarity = -1, satisfaction = -1, isFallbackMode = false }) {
  const { label: satLabel, colorClass: satColor } = getSatisfactionLabel(satisfaction);
  const satTooltip = getSatisfactionTooltip(satisfaction);

  const { label: simLabel, colorClass: simColor } = getSimilarityLabel(similarity);
  const simTooltip = getSimilarityTooltip(similarity);

  return (
    <div className="flex flex-wrap items-start justify-center text-base font-bold text-gray-700 px-1 pb-2 w-full">
      {/* Similarity section - Only show when not in fallback mode */}
      {!isFallbackMode && (
        <div className="flex items-center whitespace-nowrap mr-1 mb-1">
          <span className="italic">Similarity:</span>
          <InfoTooltip
            text={simTooltip}
            position="top"
            maxWidth={280}
            mobileTitle="Similarity Level"
            icon={
              <span className={`font-bold px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap ${simColor}`}>
                {simLabel}
              </span>
            }
          />
        </div>
      )}

      {/* Satisfaction section - will wrap to next line if needed */}
      <div className="flex items-center whitespace-nowrap">
        <span className="italic">Satisfaction:</span>
        <InfoTooltip
          text={satTooltip}
          position="top"
          maxWidth={280}
          mobileTitle="Satisfaction Level"
          icon={
            <span className={`font-bold px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap ${satColor}`}>
              {satLabel}
            </span>
          }
        />
      </div>
    </div>
  );
}